import React, { Component, ErrorInfo, ReactNode } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Container
} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import RefreshIcon from '@mui/icons-material/Refresh'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    this.setState({
      error,
      errorInfo
    })

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // Log to error reporting service in production
    if (import.meta.env.PROD) {
      // TODO: Send to error reporting service (Sentry, LogRocket, etc.)
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    })
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return <>{this.props.fallback}</>
      }

      // Default error UI
      return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
                py={4}
              >
                <ErrorOutlineIcon
                  sx={{
                    fontSize: 64,
                    color: 'error.main',
                    mb: 2
                  }}
                />

                <Typography variant="h5" gutterBottom>
                  Something went wrong
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, maxWidth: 500 }}
                >
                  We're sorry, but something unexpected happened. Please try refreshing
                  the page or contact support if the problem persists.
                </Typography>

                {/* Show error details in development */}
                {import.meta.env.DEV && this.state.error && (
                  <Alert severity="error" sx={{ mb: 3, textAlign: 'left', maxWidth: 600 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Error: {this.state.error.message}
                    </Typography>
                    {this.state.errorInfo && (
                      <details style={{ marginTop: 8 }}>
                        <summary style={{ cursor: 'pointer' }}>Stack trace</summary>
                        <pre style={{ fontSize: 12, overflow: 'auto' }}>
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </details>
                    )}
                  </Alert>
                )}

                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={() => window.location.reload()}
                  >
                    Refresh Page
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={this.handleReset}
                  >
                    Try Again
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Container>
      )
    }

    return this.props.children
  }
}

// Component-specific error boundary with custom UI
export const DashboardErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const fallback = (
    <Card sx={{ p: 3 }}>
      <Alert severity="error">
        <Typography variant="subtitle1" gutterBottom>
          Unable to load dashboard
        </Typography>
        <Typography variant="body2">
          We're having trouble loading this dashboard. Please try refreshing the page.
        </Typography>
        <Button
          size="small"
          onClick={() => window.location.reload()}
          sx={{ mt: 1 }}
        >
          Refresh
        </Button>
      </Alert>
    </Card>
  )

  return (
    <ErrorBoundary fallback={fallback}>
      {children}
    </ErrorBoundary>
  )
}

// API-specific error boundary
export const APIErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const fallback = (
    <Alert severity="warning" sx={{ m: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Connection Error
      </Typography>
      <Typography variant="body2">
        Unable to connect to the server. Data shown may be cached.
      </Typography>
    </Alert>
  )

  return (
    <ErrorBoundary
      fallback={fallback}
      onError={(error) => {
        // Check if it's an API error
        if (error.message.includes('fetch') || error.message.includes('axios')) {
          console.warn('API Error:', error.message)
        }
      }}
    >
      {children}
    </ErrorBoundary>
  )
}

export default ErrorBoundary